"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

type Props = {
  endsAt: string;
  label?: string;
  size?: "compact" | "full";
  className?: string;
};

function getRemaining(endsAt: string): CountdownParts | null {
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - Date.now();
  if (diff <= 0) return null;

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function SprintCountdown({
  endsAt,
  label,
  size = "compact",
  className,
}: Props) {
  const t = useTranslations("courseDetail.sprintCountdown");
  const [remaining, setRemaining] = useState<CountdownParts | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setRemaining(getRemaining(endsAt));
    const intervalMs = prefersReducedMotion() ? 60_000 : 1_000;
    const id = window.setInterval(() => {
      setRemaining(getRemaining(endsAt));
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [endsAt]);

  const units = useMemo(() => {
    if (!remaining) return [];
    return [
      { key: "days", value: remaining.days, label: t("days") },
      { key: "hours", value: remaining.hours, label: t("hours") },
      { key: "minutes", value: remaining.minutes, label: t("minutes") },
      { key: "seconds", value: remaining.seconds, label: t("seconds") },
    ];
  }, [remaining, t]);

  if (!mounted || !remaining) return null;

  return (
    <div
      className={cn(
        "rounded-xl border border-neutral-200/90 bg-neutral-50/80",
        size === "full" ? "p-5" : "px-3 py-2",
        className,
      )}
    >
      <p
        className={cn(
          "font-semibold text-neutral-800",
          size === "full" ? "text-sm" : "text-[12px]",
        )}
      >
        {label ?? t("label")}
      </p>
      <div
        className={cn(
          "mt-2 grid gap-2",
          size === "full" ? "grid-cols-4" : "grid-cols-4",
        )}
      >
        {units.map((unit) => (
          <div
            key={unit.key}
            className="rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-center"
          >
            <span
              className={cn(
                "block font-bold tabular-nums text-neutral-950",
                size === "full" ? "text-2xl" : "text-sm",
              )}
            >
              {String(unit.value).padStart(2, "0")}
            </span>
            <span className="block text-[10px] font-medium uppercase tracking-wide text-neutral-500">
              {unit.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
